import { useFormikContext } from 'formik'
import { Grid, H, P, Wrapper } from '@farewill/ui'

import { YES_NO_OPTIONS } from 'utils/enums'
import { FuneralPlanLeadAttributes } from 'lib/models/lead'
import Input from 'components/form/input'
import RadioGroup from 'components/form/radio-group'
import SectionStatus from 'components/section-status'
import { BRAND_ID } from 'config/brands'

const InCoveredArea = (): React.ReactElement => {
  const { values, handleSubmit } = useFormikContext<FuneralPlanLeadAttributes>()

  const LABEL = {
    [BRAND_ID.farewill]:
      'Is the plan for a person who lives in mainland England or Wales?',
    [BRAND_ID.simplicity]:
      'Is the plan for a person who lives in mainland England, Wales or Scotland?',
  }
  const REASON = {
    [BRAND_ID.farewill]:
      'Our plans only cover funerals within mainland England and Wales.',
    [BRAND_ID.simplicity]:
      'Our plans only cover funerals within mainland Britain.',
  }
  const brandId = values.contact.brandId

  if (brandId === BRAND_ID.dignity) {
    return <></>
  }

  return (
    <>
      <Grid.Item>
        <RadioGroup
          name="quoteAttributes.livesInEnglandOrWales"
          options={YES_NO_OPTIONS}
          label={LABEL[brandId]}
          handleSave={() => handleSubmit()}
        />
      </Grid.Item>
      {String(values.quoteAttributes?.livesInEnglandOrWales) === 'false' && (
        <>
          <Grid.Item spanFromL={6}>
            <Input
              name="quoteAttributes.residence"
              label="Where do they live? (Optional)"
              handleSave={() => handleSubmit()}
            />
          </Grid.Item>
          <Grid.Item>
            <SectionStatus complete heading="Not eligible for plan">
              <P>{REASON[brandId]}</P>
            </SectionStatus>
          </Grid.Item>
        </>
      )}
    </>
  )
}

const QualifyingQuestions = (): React.ReactElement => {
  const { values, handleSubmit } = useFormikContext<FuneralPlanLeadAttributes>()

  return (
    <Wrapper data-testid="demands-and-needs">
      <H size="S" margin={['XL', 0, 'S']}>
        Are they eligible?
      </H>
      <Grid>
        <InCoveredArea />
        <Grid.Item>
          <RadioGroup
            name="quoteAttributes.alreadyHasFuneralPlan"
            options={YES_NO_OPTIONS}
            label="Is there already a funeral plan in place, or any other provisions to fund the funeral for this person?"
            handleSave={() => handleSubmit()}
          />
        </Grid.Item>
        {String(values.quoteAttributes?.alreadyHasFuneralPlan) === 'true' && (
          <Grid.Item>
            <SectionStatus complete heading="Discuss options with customer">
              <P>
                Explain that they might be paying twice for their funeral, which
                will be more expensive.
              </P>
            </SectionStatus>
          </Grid.Item>
        )}
        <Grid.Item>
          <RadioGroup
            name="quoteAttributes.imminentNeed"
            options={YES_NO_OPTIONS}
            label="Is the plan for someone who has received a terminal diagnosis or is receiving end of life care?"
            handleSave={() => handleSubmit()}
          />
        </Grid.Item>
        {String(values.quoteAttributes?.imminentNeed) === 'true' && (
          <Grid.Item>
            <SectionStatus complete heading="Discuss options with customer">
              <P>
                An at-need funeral might be a better option for them, because
                it’s less expensive. One of the reasons that people buy a plan
                is to protect them against rising prices, which are less likely
                within a year or two.
              </P>
              <P>
                Make sure they understand the difference between an at-need
                funeral and a plan.
              </P>
            </SectionStatus>
          </Grid.Item>
        )}
      </Grid>
    </Wrapper>
  )
}

export default QualifyingQuestions
